/**
 * Service for the technician installation photos feature.
 * Mirrors the other portal service classes (e.g. PeripheralService): a thin
 * wrapper around the shared axios `apiClient` that targets the backend
 * `installation-photos` endpoints.
 *
 * NOTE: The backend for these endpoints is not implemented yet. This client is
 * written against the API contract defined in the design document so the
 * frontend can be wired up and swapped onto the real service with no changes.
 */
class InstallationPhotoService {
  constructor(client) {
    this.apiClient = client;
  }

  /**
   * List the installation photos for a device, newest-first (metadata only).
   * Returns items shaped like:
   *   { installationImageId, deviceId, uploadedBy, createdDateUtc }
   * Image bytes are fetched per-photo via getImage().
   */
  async listByDevice(deviceId) {
    return this.apiClient.get(`installation-photos/device/${deviceId}`);
  }

  /**
   * Get the full-resolution image for a single photo as base64 image data
   * ({ encodedImage, contentType }) streamed through the API. Scoped by device.
   */
  async getImage(deviceId, installationImageId) {
    return this.apiClient.get(
      `installation-photos/device/${deviceId}/${installationImageId}/image`
    );
  }

  /**
   * Upload a single photo for a device.
   * @param {{ deviceId: string, entityId: string, encodedImage: string, imageExtension: string }} payload
   *   `encodedImage` is the base64 payload WITHOUT the `data:` prefix.
   *   The uploading A1 user is derived server-side from the auth token and is
   *   deliberately NOT sent from the client.
   */
  async upload(payload) {
    return this.apiClient.post(`installation-photos`, payload, {
      headers: { "Content-Type": "application/json" },
    });
  }

  /**
   * Delete a single installation photo by id, scoped by device.
   */
  async remove(deviceId, installationImageId) {
    return this.apiClient.delete(
      `installation-photos/device/${deviceId}/${installationImageId}`,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
  }
}

export default InstallationPhotoService;
